/**
 * Token Management Utility
 * 
 * Centralized token storage and retrieval for authenticated API calls.
 * Uses localStorage for persistence across browser sessions.
 */

import { clearAllImageCache } from './imageCache';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const PROFILE_KEY = 'user_profile';
const GOOGLE_INFO_KEY = 'google_info';

export interface StoredUser {
  id: string;
  email?: string;
  phone?: string;
  first_name: string;
  last_name: string;
  is_verified: boolean;
  business_name?: string;
  auth_provider?: string;  // "google", "email", "phone", etc.
}

/**
 * Store authentication token in localStorage
 */
export const setAuthToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store auth token:', error);
  }
};

/**
 * Retrieve authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve auth token:', error);
    return null;
  }
};

/**
 * Remove authentication token from localStorage
 */
export const removeAuthToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove auth token:', error);
  }
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return token !== null && token.length > 0;
};

/**
 * Store user data in localStorage
 */
export const setAuthUser = (user: StoredUser): void => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Failed to store user data:', error);
  }
};

/**
 * Retrieve user data from localStorage
 */
export const getAuthUser = (): StoredUser | null => {
  try {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;
    
    const user = JSON.parse(userJson);
    
    // Validate that we have a valid user object with required fields
    if (!user || typeof user !== 'object' || !user.id) {
      console.warn('Invalid user data in localStorage, clearing...');
      removeAuthUser();
      return null;
    }
    
    return user as StoredUser;
  } catch (error) {
    console.error('Failed to retrieve user data:', error);
    // Clear corrupted data
    removeAuthUser();
    return null;
  }
};

/**
 * Remove user data from localStorage
 */
export const removeAuthUser = (): void => {
  try {
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error('Failed to remove user data:', error);
  }
};

/**
 * Store both token and user data
 */
export const setAuthData = (token: string, user: StoredUser): void => {
  if (!token || token.trim() === '') {
    console.error('⚠️ Cannot store empty token');
    return;
  }
  
  if (!user || typeof user !== 'object' || !user.id) {
    console.error('⚠️ Cannot store invalid user data', user);
    return;
  }
  
  // Ensure required fields have fallback values
  const validatedUser: StoredUser = {
    id: user.id,
    email: user.email,
    phone: user.phone,
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    is_verified: user.is_verified !== undefined ? user.is_verified : false,
    business_name: user.business_name, // Optional, can be undefined
    auth_provider: user.auth_provider // Optional, can be undefined
  };
  
  setAuthToken(token);
  setAuthUser(validatedUser);
  console.log('✅ Auth data stored successfully', { 
    userId: validatedUser.id, 
    hasBusiness: !!validatedUser.business_name,
    authProvider: validatedUser.auth_provider 
  });
};

/**
 * Clear all authentication data (logout)
 */
export const clearAuthData = (): void => {
  removeAuthToken();
  removeAuthUser();
  removeUserProfile();
  // Clear image cache on logout
  clearAllImageCache();
  console.log('🧹 Cleared all auth data and image cache');
};

/**
 * Get authentication header for API requests
 */
export const getAuthHeader = (): { Authorization: string } | {} => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Store user profile data in localStorage
 */
export const setUserProfile = (profile: any): void => {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    console.log('✅ User profile stored successfully');
  } catch (error) {
    console.error('Failed to store user profile:', error);
  }
};

/**
 * Retrieve user profile data from localStorage
 */
export const getUserProfile = (): any | null => {
  try {
    const profileJson = localStorage.getItem(PROFILE_KEY);
    if (!profileJson) return null;
    
    return JSON.parse(profileJson);
  } catch (error) {
    console.error('Failed to retrieve user profile:', error);
    return null;
  }
};

/**
 * Remove user profile data from localStorage
 */
export const removeUserProfile = (): void => {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch (error) {
    console.error('Failed to remove user profile:', error);
  }
};

/**
 * Store Google OAuth info temporarily (for pending profile creation)
 */
export const setGoogleInfo = (googleInfo: {
  email: string;
  first_name: string;
  last_name: string;
  picture?: string;
}): void => {
  try {
    console.log('💾 setGoogleInfo - Storing:', googleInfo);
    console.log('💾 setGoogleInfo - Key:', GOOGLE_INFO_KEY);
    const jsonString = JSON.stringify(googleInfo);
    console.log('💾 setGoogleInfo - JSON string:', jsonString);
    localStorage.setItem(GOOGLE_INFO_KEY, jsonString);
    console.log('✅ Google info stored for profile creation');
    // Verify it was stored
    const verification = localStorage.getItem(GOOGLE_INFO_KEY);
    console.log('✅ Verification - stored value:', verification);
  } catch (error) {
    console.error('Failed to store Google info:', error);
  }
};

/**
 * Retrieve Google OAuth info from localStorage
 */
export const getGoogleInfo = (): {
  email: string;
  first_name: string;
  last_name: string;
  picture?: string;
} | null => {
  try {
    console.log('🔍 getGoogleInfo - Looking for key:', GOOGLE_INFO_KEY);
    const googleInfoJson = localStorage.getItem(GOOGLE_INFO_KEY);
    console.log('🔍 getGoogleInfo - Raw value from localStorage:', googleInfoJson);
    if (!googleInfoJson) {
      console.log('🔍 getGoogleInfo - No google_info found in localStorage');
      return null;
    }
    const parsed = JSON.parse(googleInfoJson);
    console.log('🔍 getGoogleInfo - Parsed value:', parsed);
    return parsed;
  } catch (error) {
    console.error('Failed to retrieve Google info:', error);
    return null;
  }
};

/**
 * Remove Google OAuth info from localStorage
 */
export const removeGoogleInfo = (): void => {
  try {
    localStorage.removeItem(GOOGLE_INFO_KEY);
    console.log('✅ Google info cleared');
  } catch (error) {
    console.error('Failed to remove Google info:', error);
  }
};

